import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { DatabaseProperty } from 'src/constants/databaseProperties';
import { CompetencyQuestion } from 'src/constants/voicebox';
import {
  CreationType,
  ModelAliasInfo,
  ModelInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { Database } from 'src/store/state/databases';
import * as copy from 'src/templates/copy';
import { DEFAULT_MODEL } from 'src/templates/queries/iris';
import { DanglingNamedGraphIriByModel } from 'src/utils/getDanglingNamedGraphs';

export const updateDatabaseReasoningSchemas = async ({
  database,
  dangling,
  modelAliasInfo,
  modelInfos,
  competencyQuestions,
}: {
  database: Database;
  dangling: DanglingNamedGraphIriByModel;
  modelAliasInfo?: ModelAliasInfo;
  modelInfos: ModelInfo[];
  competencyQuestions?: CompetencyQuestion[];
}): Promise<PublishProjectResponse> => {
  const databaseId = database.id;

  // Update database reasoning schemas
  const reasoningSchemas: string[] = [];

  const existingSchemas = new Set(database.models);
  existingSchemas.delete(DEFAULT_MODEL);

  modelInfos.forEach((modelInfo) => {
    reasoningSchemas.push(`${modelInfo.modelName}=${modelInfo.namedGraphIri}`);

    if (modelInfo.creationType === CreationType.REPLACE) {
      existingSchemas.delete(modelInfo.modelName);
    }
  });

  if (modelAliasInfo) {
    modelAliasInfo.namedGraphIris.forEach((namedGraphIri) => {
      reasoningSchemas.push(`${modelAliasInfo.modelName}=${namedGraphIri}`);
    });
    if (modelAliasInfo.creationType === CreationType.REPLACE) {
      existingSchemas.delete(modelAliasInfo.modelName);
    }
  }

  existingSchemas.forEach((model) => {
    database.namedGraphsByModel?.[model].forEach((namedGraph) => {
      if (!Object.values(dangling).includes(namedGraph)) {
        reasoningSchemas.push(`${model}=${namedGraph}`);
      } else {
        existingSchemas.delete(model);
      }
    });
  });

  // Add type annotation for the second argument of updateDatabaseDetails using typeof
  const options: Parameters<
    typeof databasesStardogRequestDispatchers.updateDatabaseDetails
  >[1] = {
    [DatabaseProperty.REASONING_SCHEMAS]: reasoningSchemas,
  };

  if (competencyQuestions?.length) {
    const nextQuestions = database.voiceboxExampleQuestions || [];
    competencyQuestions.forEach(({ question }) => {
      if (!nextQuestions.includes(question)) {
        nextQuestions.push(question);
      }
    });
    options[DatabaseProperty.VOICEBOX_EXAMPLE_QUESTIONS] = nextQuestions;
  }

  const setDatabaseDetailsResponse =
    await databasesStardogRequestDispatchers.updateDatabaseDetails(
      databaseId,
      options
    );

  return setDatabaseDetailsResponse.type ===
    DatabasesActionType.UPDATE_DATABASE_DETAILS_SUCCESS
    ? {
        success: true,
        action: setDatabaseDetailsResponse,
        message:
          copy.notifications.publishProject.model.updateReasoningSchemas
            .success,
      }
    : {
        success: false,
        action: setDatabaseDetailsResponse,
        message:
          copy.notifications.publishProject.model.updateReasoningSchemas
            .failure,
        actionDescription:
          copy.notifications.publishProject.model.updateReasoningSchemas
            .actionDescription,
      };
};
